// no itx for this file
import type Device from "./Device";
import KnxInstanceData, { ValueLevels } from "./KnxInstanceData";

import Logger from "../Logger";
import { copyAllProperties } from "./utils";
import { isUiModeProductAdmin } from "../uiMode";
import { ChannelType } from "./ChannelType";
import type Channel from "./Channel";
import { type RockerConfig } from "./KnxRules";
import type Button from "./Button";
import DebugOutput from "../DebugOutput";
import { noItx } from "../translationItx/itx";

const logger = new Logger();

export enum RuleIds {
  unknown = "unknown",
  onChange = "onChange",
  onPull = "onPull",
  onPullAll = "onPullAll",
  common = "common",
  setRockerConfig = "setRockerConfig",
  mapChannelType = "mapChannelType",
  mapChannelText = "mapChannelText",
  setChannelType = "setChannelType",
  mapChannelToTag = "mapChannelToTag",
  mapSignalToTag = "mapSignalToTag",
  definitions = "definitions",
  panelConfig = "panelConfig",
}

export const ruleParams = {
  onChange: ["newValue", "lastValue"],
  onPull: ["info", "device"],
  onPullAll: ["info", "device"],
  common: [],
  setRockerConfig: ["tag", "type", "channel", "channelType"],
  setChannelType: ["tag", "type", "channel", "rockerType"],
  mapChannelToTag: ["channel", "button"],
  mapSignalToTag: ["number", "signal", "button"],
  panelConfig: ["panel"],
};

export const exampleRulesCode = {
  common: `
  // for set text|name of channel
  const tagToText = {
    T01:  "xxx1", // F1/2: S1 / Text
    T01L: "xxx1L", // F1: S1y / Text
    T01R: "xxx1R", // F2: / Text

    T02:  "xxx2", // F3/4: D1 / Text
    T02L: "xxx2L", // F3: / Text
    T02R: "xxx2R", // F4: / Text
}

function pullChannelText(channelTag) {

    const channel = getChannel(channelTag)
    if (channel) {
        const text = channel.nameOrConnectedNames;
        return { value: text, level: 4 }
    }

    return undefined
}
`,
  setRockerConfig: `
  setRocker({
    mapTypeToValue: {
      "Rocker": 1, 
    "Single": 2,
    "Disabled": 0
    },
    match: /Function *(\d+)\//

})
    `,

  //   const tagToRockerConfig = { // property ids where the rocker is set in parameters
  //     T01: "xxx", // Tasten-/Funktionseinstellung / Funktion 1/2
  //     T02: "xxx", // Tasten-/Funktionseinstellung / Funktion 3/4
  // }

  // let v = 0
  // switch (type) {
  // case "Rocker":
  //     v =  1;
  //     break;

  // case "Single":
  //     v = 2;
  //     break;

  // default:
  // case "Disabled":
  //     v = 0;
  //     return type+" is not supported";
  // }

  // const propertyId = tagToRockerConfig[tag];
  // if (!propertyId){
  //     outputError ("no propertyId for ", tag)
  //     return
  // }
  // setProperty (propertyId, v, 4)
  // `,
  setChannelType: `
  // channelType 

  const tagToChannelType = {
  // property Id where the channelType is set in Parameter
    //
    // Zweitasten Funktionen
    //
    T01: "xxx1",
    T02: "xxx2",
    //
    // Einzeltasten Funtionen
    //
    T01L: "xxx1L", // F1: Einzel-Tastenfunktion
    T01R: "xxx1R", // F2: Einzel-Tastenfunktion
    T02L: "xxx2L", // F3: Einzel-Tastenfunktion
    T02R: "xxx2R", // F4: Einzel-Tastenfunktion


}
  let v = 0
switch (rockerType) {
    case "Rocker":
        switch (type) {
            case "Switch":
                v = 0;
                break;

            case "Dimmer":
                v = 1;
                break;

            case "Blind":
            case "Jalousie":
                v = 2;
                break;

            case "Scene":
                v = 3;
                break;

            case "Disabled":
                v = 255;
                break;

            case "Heating":
                v = 255;
                break;


            default:
                console.error("setChannelType:  not supported type="+type)
                return type + " is not supported";
        }
        break;

    case "Single":
        switch (type) {
            case "Switch":
                v = 0;
                break;

            case "Dimmer":
                v = 1;
                break;

            case "Blind":
            case "Jalousie":
                v = 2;
                break;

            case "Scene":
                v = 3;
                break;

            case "Disabled":
                v = 255;
                break;

            case "Heating":
                v = 255;
                break;


            default:
                console.error("setChannelType:  not supported type="+type)
                return type + " is not supported";
        }
        break;

    default:
    case "Disabled":
        v = 0;
        return rockerType + " is not supported";
}

const propertyId = tagToChannelType[tag];
if (!propertyId) {
    console.error("setChannelType: tag=tag not supported")
    return;
}
setProperty(propertyId, v, 4);       
`,
  mapChannelToTag: `
  // tag from own to ChannelTag from template 
const tagToKnxName = {
    T01: "F1/2:",
    T01L: "F1:",
    T01R: "F2:",

    T02: "F3/4:",
    T02L: "F3:",
    T02R: "F4:",
}

let channelName = channel.name;
for (let tag in tagToKnxName) {
    const name = tagToKnxName[tag];
    if (channelName.startsWith(name)) {
        console.log("mapChannelToTag: found", tag, channelName);
        return tag
    }
}
console.log("mapChannelToTag: NO match found for ", channelName);
`,
  mapSignalToTag: `
const res = mapSignal({
    
  single: /button *(\d+)/,
  notSingle: /LED/,

  rocker: /F(\d+)\w*/,
  //notRocker: ,

  statusLed: /button *(\d+) .*LED/,
  //notStatusLed: ,

  button: /button *(\d+)/,
  //notButton: ,

  logic: /logic *(\d+)/,
  //notLogic: ,

  //logicInput: ,
  //notLogicInput: ,

  //logicOutput: ,
  //notLogicOutput: ,

  //actorChannel: ,
  //notActorChannel: ,

  longPress: /long/,
  extraLongPress: /extra long/,
  temperature: /Internal Sensor/,
 // slap: ,
})

const requestChannel = getRequestChannel(res);

if (requestChannel?.channelType != "Switch") {
    res.longPress = undefined
}

return res;
`,

  definitions: `
return {
  scenes: {
    enableForChannel: /enableForChannel/,
    enable: /enable/,
    number: /number/,
    value1: /value1/,
    value2: /value2/,
  }
}
`,
};

function evalRule(rule: KnxRule, name: string, code: string, context: any, debug: boolean): any {
  let res: any = undefined;
  try {
    const params: any = [];
    const args: any = [];
    for (let c in context) {
      params.push(c);
      args.push(context[c]);
    }

    if (debug) {
      code = "debugger; \n" + code;
    }

    const myFunction = new Function(...params, code);
    res = myFunction(...args);
  } catch (ex: any) {
    logger.error(ex);
    rule.outputError(`Compilation Error: ${ex.message}\n`);
  }

  return res;
}

export default class KnxRule {
  rule_id: RuleIds = RuleIds.unknown;

  param_id?: string = "";
  param_instanceId?: string;

  _code?: string;
  lastModified = new Date();
  modifiedBy = state.loggedInUser.name;

  modifiedLocal?: boolean;

  label: string = ""; // non persistent
  param_index?: number; // non persistent nur temporär für Anzeige

  debug?: boolean;

  // non persistent nur temporär für Anzeige vor script calls und script resultaten

  debugOutput?: DebugOutput;

  _monitor?: boolean;
  get monitor(): boolean {
    if (isUiModeProductAdmin()) {
      return this._monitor || false;
    } else {
      return false;
    }
  }

  set monitor(newValue: boolean) {
    this._monitor = newValue;
    this.debugOutput = new DebugOutput(this.rule_id, !newValue);
  }

  outputError(...args: any) {
    if (isUiModeProductAdmin()) {
      if (!this.debugOutput) {
        this.debugOutput = new DebugOutput(this.rule_id, true);
      }

      this.debugOutput.outputError(...args);
    }
  }

  output(...args: any) {
    if (this.monitor) {
      this.debugOutput?.output(...args);
    }
  }

  outputSeparator() {
    if (this.monitor) {
      this.debugOutput?.outputSeparator();
    }
  }

  ////////////////////////////////////////////////////////////////////////////////////////

  constructor(rule_id?: RuleIds, param_id?: string, param_instanceId?: string) {
    this.rule_id = rule_id || RuleIds.unknown;
    this.param_id = param_id;
    this.param_instanceId = param_instanceId;
  }

  get code(): string | undefined {
    return this._code;
  }

  set code(newValue: string | undefined) {
    this._code = newValue;
    this.lastModified = new Date();
    this.modifiedBy = state.loggedInUser.name;
  }

  get key(): string {
    return this.calcId();
  }

  get params(): Array<string> {
    return ruleParams[this.rule_id];
  }

  get example(): string {
    return exampleRulesCode[this.rule_id];
  }

  toJSON(): any {
    const data = {
      rule_id: this.rule_id,
      param_id: this.param_id,
      param_instanceId: this.param_instanceId,
      code: this.code,
      lastModified: this.lastModified,
      modifiedBy: this.modifiedBy,
      label: this.label,
    };
    return data;
  }

  fromJSON(data: any | undefined): void {
    copyAllProperties(data, this);
    this.lastModified = new Date(data.lastModified);
  }

  /////////////////////////////////////////////////////////////////////////

  execute(
    allRules: Map<string, KnxRule> | undefined,
    device: Device | undefined,
    knxInstancedata: KnxInstanceData | undefined,
    id: string,
    instanceId: string,
    moduleId: string,
    contextData: any,
  ): any {
    const ruleId = this.rule_id;

    //
    // die folgenden Funktionen stehen alen scripts zum Aufruf zur Verfügung
    //

    const self = this;

    const funcs: any = {
      setProperty: function prop(fullId: string | undefined, newValue: any, level: number | undefined) {
        if (!fullId) {
          self.output(`setProperty(undefined, "${newValue}", ${level}) PropertyId is not defined`);
          return;
        } else if (knxInstancedata && device) {
          const ii = fullId.split("/");

          if (level == undefined) {
            level = ValueLevels.calculatedByRule;
          }

          if (ii[0].endsWith("*") && !knxInstancedata.fromWildCardToFullId(ii[0], ii[1])) {
            self.outputError(noItx(`setProperty("${fullId}"): wildcard property not found`));
            return;
          }

          const lastValue = knxInstancedata.getValue(ii[0], ii[1]);

          if (lastValue == undefined) {
            if (self.monitor) {
              self.output(
                `setProperty("${fullId}", "${newValue}", ${level}) Property is not defined \n PropertyId is copied to Clipboard`,
              );
              navigator.clipboard.writeText(fullId);
            }
          }

          if (knxInstancedata && newValue != undefined) {
            knxInstancedata.setValue(level, `${newValue}`, ii[0], ii[1], moduleId, true, true);

            const setValue = knxInstancedata.getValue(ii[0], ii[1]);

            if (setValue != lastValue) {
              //
              // Flag the property as "set by Estada"
              //
              if (self.monitor) {
                self.output(`setProperty("${fullId}"; "${newValue}", ${level})`);
              }

              const valueExt = device.knxInstanceData.getExtendedData(ii[0], ii[1]) || {};
              valueExt.mbe = { isSet: true };
              device.knxInstanceData.setExtendedData(valueExt, ii[0], ii[1] || "");
            } else {
              if (self.monitor) {
                self.output(`setProperty("${fullId}");  "${newValue}" is unchanged `);
              }
            }
          } else {
            if (self.monitor) {
              self.output(`setProperty("${fullId}, value=undefined`);
            }
          }
        }
      },
      getProperty: function prop(fullId: string | undefined, instanceId?: string | undefined) {
        let res: string | undefined = undefined;
        if (fullId && instanceId) {
          res = knxInstancedata?.getValue(fullId, instanceId);
        } else if (fullId) {
          const ii = fullId.split("/");
          res = knxInstancedata?.getValue(ii[0], ii[1]);
        }

        if (!res) {
          self.outputError(`getProperty(${fullId}, ${instanceId}) -> Property not found`);
        }

        return res || "??";
      },

      setCustomValue: function prop(id: string, newValue: any) {
        if (knxInstancedata) {
          knxInstancedata.setValue(ValueLevels.interactive, `${newValue}`, id, "custom", "", true, true);
        }
      },

      getCustomValue: function prop(id: string) {
        return knxInstancedata?.getValue(id, "custom", true);
      },

      getChannelType: function (channelTag: string | undefined): ChannelType | undefined {
        if (channelTag) {
          const channel = device?.channels.getItemByTag(channelTag);
          if (channel) {
            return channel.channelType;
          }
        }
        return ChannelType.Unknown;
      },

      getChannel: function (channelTag: string | undefined): Channel | undefined {
        if (channelTag) {
          const channel = device?.channels.getItemByTag(channelTag) as Channel | undefined;
          return channel;
        }

        return undefined;
      },

      setChannelType: function (channelTag: string | undefined, newValue: string) {
        if (channelTag) {
          const channel = device?.channels.getItemByTag(channelTag);
          if (channel) {
            channel.channelType = newValue as ChannelType;
          }
        }
      },

      getRockerConfig(rockerNumber: number | undefined): RockerConfig | undefined {
        if (rockerNumber != undefined) {
          return (device as Button).getRockerConfig(rockerNumber);
        } else {
          return undefined;
        }
      },

      SignalToTag(mapping: any) {
        const channel = contextData.channel as Channel;

        const channelSignals = channel.signals.sortIntBy("number");
        const firstSignal = channelSignals[0];

        if (firstSignal) {
          const firstSignalNumber = parseInt(firstSignal.number);

          for (const tag in mapping) {
            if (firstSignalNumber >= mapping[tag][0] && firstSignalNumber <= mapping[tag][1]) {
              return tag;
            }
          }
        }
      },

      SignalNumberToTag(signalNumber: number, mapping: any) {
        for (const tag in mapping) {
          if (signalNumber >= mapping[tag][0] && signalNumber <= mapping[tag][1]) {
            return tag;
          }
        }
      },

      output(...args: any) {
        if (self.monitor) {
          self.output(...args);
        }
      },

      outputError(...args: any) {
        if (isUiModeProductAdmin()) {
          self.outputError(...args);
        }
      },
    };

    const context: any = { ...funcs, ...contextData };

    const commonCode = allRules?.get(RuleIds.common)?.code;

    let res: any;
    if (this.code) {
      logger.log(`execute ${this.rule_id}`);
      // res = evalRule2(this.code, vars);

      let code = this.code;
      if (commonCode) {
        code = commonCode + "\n  // =============\n" + code;
      }

      res = evalRule(this, this.rule_id, code, context, this.debug || false);

      return res;
    }

    return undefined;
  }

  calcId(): string {
    return KnxRule.calcId(this.rule_id, this.param_id, this.param_instanceId);
  }
  static calcId(ruleId: RuleIds, id: string | undefined, instanceId: string | undefined): string {
    if (id && instanceId) {
      return `${ruleId}-${id}-${instanceId}`;
    } else if (id) {
      return `${ruleId}-${id}`;
    } else {
      return ruleId;
    }
  }
}
